import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Save, Ticket, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { BackButton } from "@/components/BackButton";
import { useAuth } from "@/hooks/useAuth";
import { useProfileT } from "@/lib/profile-i18n";
import { getMyProfile, updateMyProfile, type MyProfile } from "@/lib/profile.functions";
import { KycSection } from "@/components/KycSection";

export const Route = createFileRoute("/profil")({
  ssr: false,
  component: ProfilPage,
  head: () => ({
    meta: [
      { title: "Můj profil — Cerveza.International" },
      { name: "description", content: "Upravte informace na svém veřejném profilu." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Můj profil — Cerveza.International" },
      { property: "og:description", content: "Upravte informace na svém veřejném profilu." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

type FormState = {
  fullName: string;
  headline: string;
  bio: string;
  city: string;
  country: string;
  websiteUrl: string;
  publicProfile: boolean;
};

const toForm = (p: MyProfile): FormState => ({
  fullName: p.fullName ?? "",
  headline: p.headline ?? "",
  bio: p.bio ?? "",
  city: p.city ?? "",
  country: p.country ?? "",
  websiteUrl: p.websiteUrl ?? "",
  publicProfile: p.publicProfile,
});

function ProfilPage() {
  const t = useProfileT();
  const { user, hasRole, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fetchProfile = useServerFn(getMyProfile);
  const saveProfile = useServerFn(updateMyProfile);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/auth", search: { redirect: "/profil" } });
  }, [loading, user, navigate]);

  const { data, isLoading } = useQuery({
    queryKey: ["my-profile", user?.id],
    enabled: !!user,
    queryFn: () => fetchProfile({}),
  });

  const [form, setForm] = useState<FormState | null>(null);
  useEffect(() => {
    if (data && !form) setForm(toForm(data));
  }, [data, form]);

  const mut = useMutation({
    mutationFn: (f: FormState) => saveProfile({ data: f }),
    onSuccess: () => {
      toast.success(t("saved"));
      qc.invalidateQueries({ queryKey: ["my-profile", user?.id] });
      if (user) qc.invalidateQueries({ queryKey: ["public-user-profile", user.id] });
    },
    onError: (e: Error) => toast.error(t("saveError", { msg: e.message })),
  });

  if (loading || !user) {
    return (
      <div className="grid min-h-[40vh] place-items-center text-sm text-muted-foreground">
        {t("loading")}
      </div>
    );
  }

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
      <BackButton />

      <div>
        <h1 className="font-serif text-3xl text-brand-earth">{t("title")}</h1>
        <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
      </div>

      {hasRole("vakant_owner") && (
        <Card className="border-primary/30">
          <CardContent className="flex flex-wrap items-center gap-3 p-4">
            <Ticket className="h-5 w-5 text-primary" aria-hidden />
            <Badge variant="secondary">{t("vakantOwner")}</Badge>
            <span className="text-sm">
              {t("vacantsCount", { count: data?.vakantyPurchased ?? 0 })}
            </span>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-4 p-4 sm:p-6">
          {isLoading || !form ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("loading")}
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="fullName">{t("fullName")}</Label>
                <Input
                  id="fullName"
                  value={form.fullName}
                  onChange={(e) => set("fullName", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="headline">{t("headline")}</Label>
                <Input
                  id="headline"
                  placeholder={t("headlinePh")}
                  value={form.headline}
                  onChange={(e) => set("headline", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bio">{t("bio")}</Label>
                <Textarea
                  id="bio"
                  rows={5}
                  placeholder={t("bioPh")}
                  value={form.bio}
                  onChange={(e) => set("bio", e.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="city">{t("city")}</Label>
                  <Input id="city" value={form.city} onChange={(e) => set("city", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="country">{t("country")}</Label>
                  <Input
                    id="country"
                    value={form.country}
                    onChange={(e) => set("country", e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="websiteUrl">{t("website")}</Label>
                <Input
                  id="websiteUrl"
                  inputMode="url"
                  placeholder="https://"
                  value={form.websiteUrl}
                  onChange={(e) => set("websiteUrl", e.target.value)}
                />
              </div>
              <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div>
                  <Label htmlFor="publicProfile">{t("publicProfile")}</Label>
                  <p className="text-xs text-muted-foreground">{t("publicProfileHint")}</p>
                </div>
                <Switch
                  id="publicProfile"
                  checked={form.publicProfile}
                  onCheckedChange={(v) => set("publicProfile", v)}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={() => mut.mutate(form)} disabled={mut.isPending}>
                  {mut.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  {t("save")}
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/uzivatel/$userId" params={{ userId: user.id }}>
                    {t("viewPublic")} <ExternalLink className="ml-2 h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <KycSection userId={user.id} />
    </div>
  );
}
